import { createContext, useCallback, useContext, useRef, useState } from "react";
import type { ReactNode } from "react";

export interface ConfirmOptions {
  title?: string;
  message?: string;
  btnText?: string;
  danger?: boolean;
}

type ConfirmFn = (opts?: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | undefined>(undefined);

interface PendingConfirm {
  opts: ConfirmOptions;
  resolve: (ok: boolean) => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const pendingRef = useRef<PendingConfirm | null>(null);
  pendingRef.current = pending;

  const confirm = useCallback<ConfirmFn>((opts = {}) => {
    return new Promise<boolean>((resolve) => {
      setPending({ opts, resolve });
    });
  }, []);

  const close = useCallback((ok: boolean) => {
    const cur = pendingRef.current;
    if (cur) {
      cur.resolve(ok);
    }
    setPending(null);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending && (
        <div
          className="modal-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) close(false);
          }}
          role="dialog"
          aria-modal="true"
        >
          <div className="modal" style={{ maxWidth: 400 }}>
            <h3>{pending.opts.title ?? "Are you sure?"}</h3>
            <p style={{ fontSize: 13, color: "var(--text2)", marginBottom: 20 }}>
              {pending.opts.message ?? "This action cannot be undone."}
            </p>
            <div className="modal-actions">
              <button type="button" className="btn btn-outline" onClick={() => close(false)}>
                Cancel
              </button>
              <button
                type="button"
                className={pending.opts.danger ? "btn btn-danger" : "btn btn-primary"}
                onClick={() => close(true)}
              >
                {pending.opts.btnText ?? "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used inside ConfirmProvider");
  return ctx;
}
